import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnInit, signal, ViewChild } from '@angular/core';
import { PAGINATION_CONSTANTS } from '../../../../../../constants/pagination.constants';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { Checkbox, CheckboxModule } from 'primeng/checkbox';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { Table, TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom } from 'rxjs';
import { CampaignsService } from '../../../../../../services/api/generated/campaigns/campaigns.service';
import { CustomFilterService } from '../../../../../../services/custom-filter.service';
import { buildFilterOptionsWithEmpty, EMPTY_VALUE_PLACEHOLDER } from '../../../../../../services/filter-options.service';
import { buildLocationFilterOptions, UsedLocation } from '../../../../../../utils/location-filter';
import { SelectOption } from '../../../../../../types/entity-definition.interface';

// MultilingualJsonInterceptor strips the I18n suffix at runtime, so the wire shape diverges from the OpenAPI spec.
interface PlanningMeasureDto {
  measureId: number;
  measureName: string;
  measureTypeName: string | null;
  priority: boolean | null;
  localisationName: string | null;
  campaignMeasureId: number | null;
  planned: boolean;
  hasValidFormula: boolean;
}

interface PlanningAuscultationDto {
  infoId: number;
  infoName: string;
  priority: boolean | null;
  localisationName: string | null;
  campaignInfoId: number | null;
  planned: boolean;
}

interface PlanningDataResponse {
  measures: PlanningMeasureDto[];
  auscultations: PlanningAuscultationDto[];
}

type PlanningDataType = 'measures' | 'auscultations' | 'all';
import { PermissionFacadeService } from '../../../../../../services/permission-facade.service';
import { CampaignInfoStore } from '../../../../../../stores/campaign-info.store';
import { CampaignMeasureStore } from '../../../../../../stores/campaign-measure.store';
import { CampaignStore } from '../../../../../../stores/campaign.store';
import { InfoLocationStore } from '../../../../../../stores/info-location.store';
import { LocationStore } from '../../../../../../stores/location.store';
import { MeasureLocationStore } from '../../../../../../stores/measure-location.store';
import { CampaignPlanningCacheService } from './campaign-planning-cache.service';
import { CampaignHistoryService, CampaignActionContext, PlanningModificationDetail } from '../sub-services/campaign-history.service';
import { CampaignPageStore } from '../sub-services/campaign-page.store';
import { CesButtonSecondaryComponent } from '../../../../../ui/ces-buttons/ces-button-secondary.component';
import { CesButtonTertiaryComponent } from '../../../../../ui/ces-buttons/ces-button-tertiary.component';
import { CesIconComponent } from '../../../../../ui/ces-icon/ces-icon.component';
import { CesGridSkeletonComponent } from '../../../../../ui/skeletons/grid-skeleton.component';
import { SuspendedMeasureIconComponent } from '../../../../../ui/suspended-measure-icon/suspended-measure-icon.component';

import { CesTooltipAutoDismissDirective } from '../../../../../../directives/ces-tooltip-auto-dismiss.directive';
export interface PlanningDialogData {
  campaignId: number;
  assetId: number;
  mode: 'measures' | 'auscultations';
}

export interface PlanningDialogResult {
  changed: boolean;
}

export interface PlanningRow {
  id: number;
  typeName: string;
  name: string;
  obligatoire: boolean;
  localisation: string;
  locationIds: number[];
  planned: boolean;
  measureId?: number;
  campaignMeasureId?: number;
  auscultationId?: number;
  campaignInfoId?: number;
  hasValidFormula: boolean;
}

type DialogStatus = 'loading' | 'ready' | 'saving' | 'error';

interface DialogState {
  status: DialogStatus;
  rows: PlanningRow[];
}

// Dual-host planning grid: opened as a DynamicDialog from the campaign tabs, or
// embedded in a page (campaign model detail) where the dialog ref is absent and
// the context arrives through inputs instead.
@Component({
  selector: 'app-campaign-planning',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CesTooltipAutoDismissDirective,
    FormsModule,
    TranslocoModule,
    ButtonModule,
    CheckboxModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    TableModule,
    ToastModule,
    TooltipModule,
    CesButtonTertiaryComponent,
    CesButtonSecondaryComponent,
    CesIconComponent,
    CesGridSkeletonComponent,
    SuspendedMeasureIconComponent,
  ],
  providers: [MessageService],
  templateUrl: './campaign-planning.component.html',
  styleUrl: './campaign-planning.component.scss',
})
export class CampaignPlanningComponent implements OnInit {
  @ViewChild('dt') private table!: Table;
  @ViewChild('selectAllCheckbox') private selectAllCheckbox?: Checkbox;

  private readonly ref = inject(DynamicDialogRef, { optional: true });
  private readonly config = inject<DynamicDialogConfig<PlanningDialogData>>(DynamicDialogConfig, { optional: true });
  private readonly data: PlanningDialogData | undefined = this.config?.data;

  readonly campaignId = input<number | null>(null);
  readonly assetId = input<number | null>(null);
  readonly mode = input<PlanningDialogData['mode'] | null>(null);
  readonly readOnly = input(false);
  // Page hosts (campaign model) opt into a planned-only default view; the dialog host always
  // keeps the full catalogue so measures can be added.
  readonly defaultPlannedOnly = input(false);
  private readonly campaigns = inject(CampaignsService);
  private readonly campaignMeasureStore = inject(CampaignMeasureStore);
  private readonly campaignInfoStore = inject(CampaignInfoStore);
  private readonly campaignStore = inject(CampaignStore);
  private readonly locationStore = inject(LocationStore);
  private readonly measureLocationStore = inject(MeasureLocationStore);
  private readonly infoLocationStore = inject(InfoLocationStore);
  private readonly customFilterService = inject(CustomFilterService);
  private readonly planningCache = inject(CampaignPlanningCacheService);
  private readonly historyService = inject(CampaignHistoryService);
  private readonly pageStore = inject(CampaignPageStore);
  private readonly messageService = inject(MessageService);
  private readonly permissionFacade = inject(PermissionFacadeService);
  private readonly translateService = inject(TranslocoService);
  private readonly translations = toSignal(this.translateService.selectTranslation());

  private readonly state = signal<DialogState>({ status: 'loading', rows: [] });
  private readonly selectedIds = signal<Set<number>>(new Set());
  private readonly locationNameById = signal<Map<number, string>>(new Map());

  readonly isLoading = computed(() => this.state().status === 'loading');
  readonly isSaving = computed(() => this.state().status === 'saving');
  readonly rows = computed(() => this.state().rows);
  // Real cardinality of the model (what it actually plans), surfaced in the page header.
  readonly plannedCount = computed(() => this.rows().filter(r => r.planned).length);

  // Mirror the rendered columns so the loading skeleton matches the grid (no column-count flash):
  // the leading selection column is dropped when read-only, the planned column when planned-only.
  readonly skeletonColumns = computed(() => {
    this.translations();
    const columns: string[] = [];
    if (!this.readOnly()) {
      columns.push('');
    }
    columns.push(this.translateService.translate('common.labels.name'));
    if (this.planningMode() === 'measures') {
      columns.push(this.translateService.translate('common.labels.type'));
    }
    columns.push(
      this.translateService.translate('common.labels.mandatory'),
      this.translateService.translate('entities.location.singular'),
    );
    if (!this.defaultPlannedOnly()) {
      columns.push(this.translateService.translate('common.labels.planned'));
    }
    return columns;
  });

  // Empty-state cell spans every rendered column (selection − when read-only, planned − when planned-only).
  readonly emptyColspan = computed(
    () => (this.planningMode() === 'measures' ? 6 : 5) - (this.defaultPlannedOnly() ? 1 : 0) - (this.readOnly() ? 1 : 0),
  );

  readonly planningMode = computed(() => this.data?.mode ?? this.mode() ?? 'measures');
  readonly targetCampaignId = computed(() => this.data?.campaignId ?? this.campaignId() ?? 0);
  readonly targetAssetId = computed(() => this.data?.assetId ?? this.assetId() ?? 0);
  readonly isDialog = !!this.ref;
  // Declarative initial filter: re-applies on every mount (survives tab switches) and keeps
  // table.filteredValue correct for any select-all. Empty on the dialog host so the catalogue
  // stays complete for adding measures.
  readonly tableFilters = computed<Record<string, { value: unknown; matchMode: string }>>(() =>
    this.defaultPlannedOnly() && !this.isDialog ? { planned: { value: true, matchMode: 'equals' } } : {},
  );
  protected readonly PAGINATION_CONSTANTS = PAGINATION_CONSTANTS;
  readonly hasMeasureReadAccess = this.permissionFacade.hasReadAccess('configuration/measures');

  readonly typeOptions = computed(() =>
    buildFilterOptionsWithEmpty(
      this.rows().map(r => r.typeName),
      this.translateService.translate('common.dialogs.selectMeasures.filters.noType'),
      { forceEmpty: false },
    ),
  );

  readonly locationOptions = computed<SelectOption[]>(() => {
    const names = this.locationNameById();
    const usedIds = new Set<number>();
    let hasUnlocated = false;
    for (const row of this.rows()) {
      if (row.locationIds.length === 0) {
        hasUnlocated = true;
      } else {
        row.locationIds.forEach(id => usedIds.add(id));
      }
    }
    const used: UsedLocation[] = [...usedIds].map(id => ({ id, name: names.get(id) ?? '' }));
    return buildLocationFilterOptions(used, this.translateService.translate('common.dialogs.selectMeasures.filters.noLocation'), {
      forceEmpty: hasUnlocated,
    });
  });

  readonly selectableRows = computed(() => this.rows().filter(r => this.isRowSelectable(r)));
  readonly selectedRows = computed(() => {
    const ids = this.selectedIds();
    return this.rows().filter(r => ids.has(r.id) && this.isRowSelectable(r));
  });
  readonly selectedCount = computed(() => this.selectedRows().length);
  readonly someSelected = computed(() => this.selectedCount() > 0);
  readonly allSelectableSelected = computed(() => {
    const selectable = this.selectableRows();
    const ids = this.selectedIds();
    return selectable.length > 0 && selectable.every(r => ids.has(r.id));
  });

  isSelected(row: PlanningRow): boolean {
    return this.selectedIds().has(row.id);
  }

  isRowSelectable(row: PlanningRow): boolean {
    // A mandatory measure that is already planned can't be unplanned, but one not yet
    // planned (e.g. its formula only became valid later) must stay selectable so it can
    // be added — otherwise it gets stranded outside the campaign with no recovery path.
    return !this.readOnly() && (row.hasValidFormula || row.planned) && !(row.obligatoire && row.planned);
  }

  readonly canPlan = computed(() => this.selectedRows().some(r => !r.planned));
  readonly canUnplan = computed(() => this.selectedRows().some(r => r.planned));

  private readonly hasChanges = signal(false);

  getSelectRowAriaLabel(name: string): string {
    return this.translateService.translate('common.accessibility.selectItem', { name });
  }

  constructor() {
    // Live refresh behind the planning dialog: when the dialog (a separate instance) plans/unplans
    // the same campaign+mode, refresh this page-hosted list — same intent as the real-campaign tabs
    // reloading their parent on each change. Soft reload keeps the rows visible (no skeleton flash).
    let mutationBaseline: number | null = null;
    effect(() => {
      const campaignId = this.targetCampaignId();
      const tick = this.planningCache.mutationTick(campaignId, this.planningMode());
      if (this.isDialog || !campaignId) return;
      if (mutationBaseline === null) {
        mutationBaseline = tick;
        return;
      }
      if (tick !== mutationBaseline) {
        mutationBaseline = tick;
        // Defer out of the effect so its async signal writes don't run during reactive execution.
        queueMicrotask(() => void this.loadData(true));
      }
    });
  }

  ngOnInit(): void {
    this.customFilterService.ensureFiltersRegistered();
    void this.loadData();
  }

  // Page hosts call this after a dialog mutated the snapshot: the dialog bypasses the cache,
  // so it must be invalidated before refetching the now-stale page rows. Soft = no skeleton flash.
  async reload(): Promise<void> {
    this.planningCache.invalidate(this.targetCampaignId(), this.planningMode());
    await this.loadData(true);
  }

  // soft = refresh in place (no skeleton, no cache replay) so a live update behind the dialog
  // swaps rows without a visible flash.
  private async loadData(soft = false): Promise<void> {
    const campaignId = this.targetCampaignId();
    const assetId = this.targetAssetId();
    const mode = this.planningMode();
    if (!campaignId) return;

    // Page hosts re-mount on every tab switch; replay the cached rows so the
    // switch is instant. Dialog hosts always fetch fresh.
    if (!this.isDialog && !soft) {
      const cached = this.planningCache.get(campaignId, mode);
      if (cached) {
        this.locationNameById.set(cached.locationNames);
        this.selectedIds.set(new Set());
        this.state.set({ status: 'ready', rows: cached.rows });
        return;
      }
    }

    if (!soft) {
      this.state.set({ status: 'loading', rows: [] });
    }
    this.selectedIds.set(new Set());
    try {
      const type: PlanningDataType = mode === 'measures' ? 'measures' : 'auscultations';
      const data = await firstValueFrom(
        this.campaigns.getCampaignPlanningDataResource<PlanningDataResponse>({ id: campaignId }, { type }),
      );
      const locationsByItem = await this.loadLocations(assetId, mode, data);
      const rows =
        mode === 'measures'
          ? data.measures.map(m => this.mapMeasure(m, locationsByItem.get(m.measureId) ?? []))
          : data.auscultations.map(a => this.mapAuscultation(a, locationsByItem.get(a.infoId) ?? []));
      this.state.set({ status: 'ready', rows });
      this.syncPageCache();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.translate('common.toasts.error'),
        detail: this.translateService.translate('common.planning.loadError'),
      });
      this.state.set({ status: 'error', rows: [] });
    }
  }

  // Localisation ids per item, plus each location's name for the filter options. Measures join
  // through measure_location, auscultations through info_location; both reference the same asset
  // `location` table, so a single LocationStore fetch resolves every name.
  private async loadLocations(
    assetId: number,
    mode: 'measures' | 'auscultations',
    data: PlanningDataResponse,
  ): Promise<Map<number, number[]>> {
    const byItem = new Map<number, number[]>();
    if (!assetId) return byItem;

    const push = (key: number, locationId: number): void => {
      const existing = byItem.get(key);
      existing ? existing.push(locationId) : byItem.set(key, [locationId]);
    };

    if (mode === 'measures') {
      const [locations, measureLocations] = await Promise.all([
        this.locationStore.getByAssetId(assetId),
        this.measureLocationStore.getByAssetId(assetId),
      ]);
      this.locationNameById.set(new Map(locations.map(location => [location.id, location.name])));
      for (const ml of measureLocations) push(ml.measureId, ml.locationId);
    } else {
      const infoIds = data.auscultations.map(a => a.infoId);
      const [locations, infoLocations] = await Promise.all([
        this.locationStore.getByAssetId(assetId),
        this.infoLocationStore.getByInfoIds(infoIds),
      ]);
      this.locationNameById.set(new Map(locations.map(location => [location.id, location.name])));
      for (const il of infoLocations) push(il.infoId, il.locationId);
    }
    return byItem;
  }

  // MultilingualJsonInterceptor converts null I18n payloads to "", so blanks must collapse to undefined for `??` to fire.
  private nonBlank(value: string | null | undefined): string | undefined {
    const trimmed = value?.trim();
    if (!trimmed) return undefined;
    return trimmed;
  }

  private mapMeasure(m: PlanningMeasureDto, locationIds: number[]): PlanningRow {
    return {
      id: m.measureId,
      typeName: this.nonBlank(m.measureTypeName) ?? EMPTY_VALUE_PLACEHOLDER,
      name: this.nonBlank(m.measureName) ?? this.translateService.translate('common.planning.unnamed'),
      obligatoire: m.priority ?? false,
      localisation: this.nonBlank(m.localisationName) ?? EMPTY_VALUE_PLACEHOLDER,
      locationIds,
      planned: m.planned,
      measureId: m.measureId,
      ...(m.campaignMeasureId != null && { campaignMeasureId: m.campaignMeasureId }),
      hasValidFormula: m.hasValidFormula ?? true,
    };
  }

  private mapAuscultation(a: PlanningAuscultationDto, locationIds: number[]): PlanningRow {
    return {
      id: a.infoId,
      typeName: this.translateService.translate('entities.auscultationInformation.singular'),
      name: this.nonBlank(a.infoName) ?? this.translateService.translate('common.planning.unnamed'),
      obligatoire: a.priority ?? false,
      localisation: this.nonBlank(a.localisationName) ?? EMPTY_VALUE_PLACEHOLDER,
      locationIds,
      planned: !!a.campaignInfoId,
      auscultationId: a.infoId,
      ...(a.campaignInfoId != null && { campaignInfoId: a.campaignInfoId }),
      hasValidFormula: true,
    };
  }

  displayCell(value: string): string {
    return value === EMPTY_VALUE_PLACEHOLDER ? '-' : value;
  }

  trackById(_index: number, row: PlanningRow): number {
    return row.id;
  }

  onSelectAllChange(event: { checked?: boolean }): void {
    const checked = event.checked ?? false;
    const filtered = this.table?.filteredValue as PlanningRow[] | null | undefined;
    const visibleRows: PlanningRow[] = filtered ?? this.state().rows;
    const selectableVisibleIds = visibleRows.filter(r => this.isRowSelectable(r)).map(r => r.id);

    this.selectedIds.update(ids => {
      const newIds = new Set(ids);
      selectableVisibleIds.forEach(id => (checked ? newIds.add(id) : newIds.delete(id)));
      return newIds;
    });
    // PrimeNG's onClick flips its internal `model` to trueValue before this handler runs.
    // NgModel skips writeValue when [ngModel] stays at the same value across CD cycles, so the
    // internal `model` stays stuck at `true`, surfacing as a check icon once [indeterminate] flips off.
    // Forcing writeValue directly resyncs PrimeNG to the canonical state.
    this.selectAllCheckbox?.writeValue(this.allSelectableSelected());
  }

  handleRowCheckboxClick(event: MouseEvent, row: PlanningRow): void {
    event.stopPropagation();
    if (!this.isRowSelectable(row)) return;

    this.selectedIds.update(ids => {
      const newIds = new Set(ids);
      newIds.has(row.id) ? newIds.delete(row.id) : newIds.add(row.id);
      return newIds;
    });
  }

  onFilterInput(event: Event, filterCallback: (value: string) => void): void {
    const value = (event.target as HTMLInputElement).value;
    filterCallback(value);
  }

  async onPlan(): Promise<void> {
    const campaignId = this.targetCampaignId();
    if (!campaignId) return;

    const toPlan = this.selectedRows().filter(r => !r.planned);
    if (toPlan.length === 0) {
      this.messageService.add({
        severity: 'info',
        summary: this.translateService.translate('common.toasts.info'),
        detail: this.translateService.translate('common.planning.allAlreadyPlanned'),
      });
      return;
    }

    this.state.update(s => ({ ...s, status: 'saving' }));

    try {
      let newCampaignInfoIds: Map<number, number> | undefined;
      // The server decides what may be planned, so only the rows it accepted may be
      // logged and flipped locally.
      let acceptedRows = toPlan;
      let refusedRows: PlanningRow[] = [];

      if (this.planningMode() === 'measures') {
        const bulkItems = toPlan.flatMap(m => (m.measureId !== undefined ? [{ measureId: m.measureId, planned: true }] : []));
        const response = await this.campaignMeasureStore.bulkUpsert(campaignId, bulkItems);
        const refusedIds = new Set(response.invalidMeasureIds ?? []);
        // A row with no measureId never reached bulkItems, so it was never planned either.
        refusedRows = toPlan.filter(m => m.measureId === undefined || refusedIds.has(m.measureId));
        acceptedRows = toPlan.filter(m => m.measureId !== undefined && !refusedIds.has(m.measureId));
      } else {
        const bulkItems = toPlan.flatMap(a => (a.auscultationId !== undefined ? [{ infoId: a.auscultationId, planned: true }] : []));
        const response = await this.campaignInfoStore.bulkCreate(campaignId, bulkItems);
        newCampaignInfoIds = new Map(response.results.map((r: { id: number; infoId: number; planned: boolean }) => [r.infoId, r.id]));
      }

      if (acceptedRows.length > 0) {
        await this.logPlanningChanges(campaignId, acceptedRows, 'plan');
        this.hasChanges.set(true);
        this.updateRowsLocally(acceptedRows, true, newCampaignInfoIds);
        this.updateParentCache(acceptedRows, true);
      }

      // A refusal is the outcome worth reading; a green success next to it would only
      // bury what was left out.
      if (refusedRows.length > 0) this.notifyNotPlanned(refusedRows);
      else this.notifySuccess();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.translate('common.toasts.error'),
        detail: this.translateService.translate('common.planning.planningError'),
      });
      return;
    } finally {
      this.state.update(s => ({ ...s, status: 'ready' }));
    }

    this.closeDialog();
  }

  async onUnplan(): Promise<void> {
    const campaignId = this.targetCampaignId();
    if (!campaignId) return;

    const toUnplan = this.selectedRows().filter(r => r.planned);
    if (toUnplan.length === 0) {
      this.messageService.add({
        severity: 'info',
        summary: this.translateService.translate('common.toasts.info'),
        detail: this.translateService.translate('common.planning.noItemsToUnplan'),
      });
      return;
    }

    this.state.update(s => ({ ...s, status: 'saving' }));

    try {
      if (this.planningMode() === 'measures') {
        const bulkItems = toUnplan.flatMap(m => (m.measureId !== undefined ? [{ measureId: m.measureId, planned: false }] : []));
        await this.campaignMeasureStore.bulkUpsert(campaignId, bulkItems);
      } else {
        const idsToDelete = toUnplan.flatMap(a => (a.campaignInfoId !== undefined ? [a.campaignInfoId] : []));
        if (idsToDelete.length > 0) {
          await this.campaignInfoStore.bulkDelete(idsToDelete);
        }
      }

      await this.logPlanningChanges(campaignId, toUnplan, 'unplan');
      this.hasChanges.set(true);
      this.updateRowsLocally(toUnplan, false);
      this.updateParentCache(toUnplan, false);
      this.notifySuccess();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.translate('common.toasts.error'),
        detail: this.translateService.translate('common.planning.unplanningError'),
      });
      this.state.update(s => ({ ...s, status: 'ready' }));
      return;
    }

    this.closeDialog();
  }

  private updateRowsLocally(modifiedRows: PlanningRow[], planned: boolean, newCampaignInfoIds?: Map<number, number>): void {
    const modifiedIds = new Set(modifiedRows.map(r => r.id));

    this.selectedIds.set(new Set());
    this.selectAllCheckbox?.writeValue(false);

    const tableElement = this.table?.el?.nativeElement as HTMLElement | undefined;
    const scrollContainer = tableElement?.querySelector<HTMLElement>('.p-datatable-table-container') ?? null;
    const scrollTop = scrollContainer?.scrollTop ?? 0;

    this.state.update(s => ({
      ...s,
      status: 'ready',
      rows: s.rows.map(r => {
        if (!modifiedIds.has(r.id)) return r;
        if (!planned) {
          const { campaignInfoId: _omit, ...rest } = r;
          return { ...rest, planned: false };
        }
        const resolvedId = r.auscultationId !== undefined ? newCampaignInfoIds?.get(r.auscultationId) : undefined;
        const finalCampaignInfoId = resolvedId ?? r.campaignInfoId;
        return {
          ...r,
          planned: true,
          ...(finalCampaignInfoId !== undefined && { campaignInfoId: finalCampaignInfoId }),
        };
      }),
    }));

    if (scrollTop > 0) {
      requestAnimationFrame(() => {
        if (scrollContainer) {
          scrollContainer.scrollTop = scrollTop;
        }
      });
    }

    this.syncPageCache();
    // Notify any page host showing the same campaign+mode to refresh live behind the dialog.
    this.planningCache.markMutated(this.targetCampaignId(), this.planningMode());
  }

  // Page hosts only: mirror the current rows so the next tab mount replays them.
  private syncPageCache(): void {
    if (this.isDialog) return;
    this.planningCache.set(this.targetCampaignId(), this.planningMode(), this.state().rows, this.locationNameById());
  }

  private updateParentCache(rows: PlanningRow[], planned: boolean): void {
    // The page store mirrors the campaign-details context; skip the sync when the
    // dialog edits a detached campaign (e.g. a model snapshot from the models list).
    if (this.pageStore.campaignId() !== this.targetCampaignId()) return;
    if (this.planningMode() === 'measures') {
      const measureIds = rows.flatMap(r => (r.measureId !== undefined ? [r.measureId] : []));
      this.campaignMeasureStore.updateDetailedMeasuresPlannedStatus(measureIds, planned);
      this.pageStore.loadMeasuresData(true).catch(() => {});
    } else {
      this.pageStore.loadAuscultationData(true).catch(() => {});
    }
  }

  private async logPlanningChanges(campaignId: number, items: PlanningRow[], action: 'plan' | 'unplan'): Promise<void> {
    const campaign = this.campaignStore.byId()(campaignId);
    if (!campaign) return;

    const isMeasureMode = this.planningMode() === 'measures';

    const modifications: PlanningModificationDetail[] = items.flatMap(item => {
      const itemId = isMeasureMode ? item.measureId : item.auscultationId;
      if (itemId === undefined) return [];
      return [
        {
          itemType: isMeasureMode ? 'measure' : 'auscultation',
          itemId,
          itemName: item.name,
          action,
        },
      ];
    });
    if (modifications.length === 0) return;

    const context: CampaignActionContext = {
      campaignId,
      ...(campaign.timestamp && { campaignName: this.historyService.formatLogDate(campaign.timestamp) }),
      assetId: campaign.assetId,
    };

    try {
      await this.historyService.logPlanningUpdate(context, modifications);
      this.pageStore.invalidateHistoryCache();
    } catch {}
  }

  // Success path emitted no feedback before; the toast is announced via aria-live so the
  // planned/unplanned outcome is conveyed to assistive tech, not just the icon colour flip.
  private notifySuccess(): void {
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.translate('common.toasts.success'),
      detail: this.translateService.translate('common.toasts.updateSuccess'),
    });
  }

  private notifyNotPlanned(rows: PlanningRow[]): void {
    this.messageService.add({
      severity: 'warn',
      summary: this.translateService.translate('common.toasts.warning'),
      detail: this.translateService.translate('common.planning.notPlannedNoValidFormula', {
        names: rows.map(r => r.name).join(', '),
      }),
    });
  }

  cancel(): void {
    this.closeDialog();
  }

  // Closing on a successful plan/unplan is what makes the dialog one-shot; a failure leaves it
  // open so the selection survives the retry. No-op on the page host, which has no dialog ref.
  private closeDialog(): void {
    this.ref?.close({ changed: this.hasChanges() });
  }
}
